import React, { useEffect, useContext } from 'react';
import {
  ToggleButtonGroup, ToggleButton, Tooltip, OverlayTrigger, Stack, Accordion, Form, ButtonGroup
} from 'react-bootstrap';
import {
  togglePanel, handleClear, showErrorMessage, panelVariables,
  AccordeonHeaderButtonForSearchForm, SearchButtonToolbar
} from 'src/components/searchModal/forms/SearchModalFunctions';
import { allElnElementsForSearch } from 'src/apps/generic/Utils';
import AdvancedSearchRow from 'src/components/searchModal/forms/AdvancedSearchRow';
import ButtonGroupToggleButton from 'src/components/common/ButtonGroupToggleButton';
import DetailSearch from 'src/components/searchModal/forms/DetailSearch';
import SearchResult from 'src/components/searchModal/forms/SearchResult';
import { observer } from 'mobx-react';
import { StoreContext } from 'src/stores/mobx/RootStore';

const TextSearch = () => {
  const { searchStore, userStore } = useContext(StoreContext);
  const elnElements = allElnElementsForSearch;
  const genericElements = userStore.genericEls || [];
  const panelVars = panelVariables(searchStore);
  const accordionItemClass = searchStore.searchResultsCount > 0 ? ' with-result' : '';
  const activeSearchAccordionClass = searchStore.search_accordion_active_key === 0
    ? `active${accordionItemClass}`
    : '';
  const activeResultAccordionClass = searchStore.search_accordion_active_key === 1 ? ' active with-result' : '';

  useEffect(() => {
    const advancedValues = searchStore.advancedSearchValues;
    const length = advancedValues.length - 1;
    const lastInputRow = searchStore.advancedSearchValues[length];

    const checkSelectedElements =
      (lastInputRow.field && lastInputRow.value && lastInputRow.link) ||
      (length === 0 && lastInputRow.field && lastInputRow.value);

    if (checkSelectedElements) {
      const searchValues = {
        link: 'OR', match: 'ILIKE',
        table: advancedValues[0].table,
        element_id: advancedValues[0].element_id,
        field: '', value: '', smiles: '', sub_values: [],
        unit: '', validationState: null
      };
      searchStore.addAdvancedSearchValue(length + 1, searchValues);
    }
  }, [searchStore.advancedSearchValues, searchStore]);

  const handleChangeElement = (element) => {
    const table = elnElements.includes(element) ? element : 'elements';
    const genericElement = (!elnElements.includes(element)
      && genericElements.find(el => `${el.name}s` === element)) || {};

    searchStore.changeSearchElement({
      table,
      element_id: (genericElement.id || 0),
      element_table: element
    });
    const searchValues = { ...searchStore.advancedSearchValues[0] };
    searchValues.table = table;
    searchValues.element_id = (genericElement.id || 0);
    searchStore.addAdvancedSearchValue(0, searchValues);
  };

  const handleNumericMatchChange = (e) => {
    searchStore.changeNumericMatchValue(e.target.value);
  };

  const SelectSearchTable = () => {
    const { layout } = userStore.profile.data;

    const buttons = Object.entries(layout).filter((value) => value[1] > 0)
      .sort((a, b) => a[1] - b[1])
      .map((value) => {
        const genericElement = (genericElements && genericElements.find(el => el.name === value[0])) || {};
        if (genericElement.id === undefined && !elnElements.includes(`${value[0]}s`)) { return; }

        let iconClass = `icon-${value[0]}`;
        if (!elnElements.includes(`${value[0]}s`)) {
          iconClass = `${genericElement.icon_name} icon_generic_nav`;
        }
        const tooltip = (
          <Tooltip id="_tooltip_history" className="left_tooltip">
            {value[0].charAt(0).toUpperCase() + value[0].slice(1).replace(/_/g, ' ')}
          </Tooltip>
        );

        return (
          <ToggleButton
            key={value[0]}
            id={value[0]}
            value={`${value[0]}s`}
            variant="outline-dark"
            className={`${value[0]}s` === searchStore.searchElement.element_table ? 'active' : null}
          >
            <OverlayTrigger delayShow={500} placement="top" overlay={tooltip}>
              <i className={iconClass} />
            </OverlayTrigger>
          </ToggleButton>
        );
      });

    return (
      <ToggleButtonGroup
        type="radio"
        name="options"
        key="element-options"
        value={searchStore.searchElement.element_table}
        onChange={handleChangeElement}
        className="advanced-search-toggle-elements"
      >
        {buttons}
      </ToggleButtonGroup>
    );
  };

  const SwitchToAdvancedOrDetailSearch = () => (
      <ButtonGroup>
        <ButtonGroupToggleButton
          size="sm"
          className="advanced-detail-button-group"
          active={searchStore.searchType === 'advanced'}
          onClick={() => searchStore.changeSearchType('advanced')}
        >
          Advanced
        </ButtonGroupToggleButton>
        <ButtonGroupToggleButton
          size="sm"
          className="advanced-detail-button-group"
          active={searchStore.searchType === 'detail'}
          onClick={() => searchStore.changeSearchType('detail')}
        >
          Detail
        </ButtonGroupToggleButton>
      </ButtonGroup>
    );

  const renderDynamicRow = () => {
    let dynamicRow = (<span />);

    if (searchStore.advancedSearchValues.length > 1) {
      const addedSelections = searchStore.advancedSearchValues.filter((val, idx) => idx > 0);

      dynamicRow = addedSelections.map((_, idx) => {
        const id = idx + 1;
        return (
          <AdvancedSearchRow idx={id} key={`selection_${id}`} />
        );
      });
    }

    return dynamicRow;
  };

  return (
    <Accordion defaultActiveKey={0} activeKey={searchStore.search_accordion_active_key} className="search-modal" flush>
      <Accordion.Item eventKey={0} className={activeSearchAccordionClass}>
        <h2 className="accordion-header flex-shrink-0">
          <AccordeonHeaderButtonForSearchForm
            title={panelVars.searchTitle}
            eventKey={0}
            disabled={searchStore.search_accordion_toggle_disabled}
            callback={togglePanel(searchStore)}
          />
        </h2>
        <Accordion.Collapse eventKey={0}>
          <div className="accordion-body">
            <Stack direction="horizontal" className="advanced-search-content-header" gap={2}>
              {SelectSearchTable()}
              {SwitchToAdvancedOrDetailSearch()}
            </Stack>
            <div className="advanced-search-content-scrollable-body">
              {showErrorMessage(searchStore)}
              {
                searchStore.searchType === 'advanced' ? (
                  <>
                    <AdvancedSearchRow idx={0} key={'selection_0'} />
                    {renderDynamicRow()}
                  </>
                ) : (
                  <DetailSearch
                    key={searchStore.searchElement.element_table}
                  />
                )
              }
            </div>
            <Form className="d-flex align-items-center gap-5">
              <SearchButtonToolbar store={searchStore} />
              {
                searchStore.searchType === 'detail' && (
                  <Form.Group className="d-flex align-items-baseline gap-4">
                    <span>Change search operator for numeric Fields:</span>
                    <Form.Check
                      type="radio"
                      name="equal_higher_or_lower"
                      id="equal_higher_or_lower_higher"
                      label=">="
                      value=">="
                      checked={searchStore.numeric_match === '>='}
                      onChange={handleNumericMatchChange}
                    />
                    <Form.Check
                      type="radio"
                      name="equal_higher_or_lower"
                      id="equal_higher_or_lower_higher"
                      label="<="
                      value="<="
                      checked={searchStore.numeric_match === '<='}
                      onChange={handleNumericMatchChange}
                    />
                  </Form.Group>
                )
              }
            </Form>
          </div>
        </Accordion.Collapse>
      </Accordion.Item>
      <Accordion.Item eventKey={1} className={`${panelVars.invisibleClassName}${activeResultAccordionClass}`}>
        <h2 className="accordion-header flex-shrink-0">
          <AccordeonHeaderButtonForSearchForm
            title={panelVars.resultTitle}
            eventKey={1}
            callback={togglePanel(searchStore)}
          />
        </h2>
        <Accordion.Collapse eventKey={1} className="search-result-body">
          <div className="accordion-body">
            <SearchResult handleClear={() => handleClear(searchStore)} />
          </div>
        </Accordion.Collapse>
      </Accordion.Item>
    </Accordion>
  );
};

export default observer(TextSearch);
